import cloneDeep from 'clone-deep'
import { ContractorFieldProps, ContractorInputType, ContractorOrigin, contractorOriginOptions, InputType } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { Modal } from 'stories/components'
import { type InputOptions, capitalizeFirstLetter, InputConvert, InputValidate, normalizeOptions } from 'utils'
import { RenderInput } from 'utils/RenderInput'

import { contractorInputPropertyFields } from './constants'

interface ContractorInputPropertyModalProps {
  fieldKey: string
  fieldData: ContractorInputType
  data: ContractorFieldProps
  sectionOptions?: Record<string, string>
  onClose: (updated?: ContractorInputType) => void
}

const visibleLogicOrigins = [ContractorOrigin.GeneralInformation, ContractorOrigin.CapabilityStatement]

const buildAdditionalFieldOrigins = (data: ContractorFieldProps) => {
  const result: Record<string, { title: string; inputs: Record<string, InputType> }> = {}
  visibleLogicOrigins.forEach((origin) => {
    const inputs = (data[origin]?.inputs ?? {}) as Record<string, InputType>
    if (!Object.keys(inputs).length) return
    result[origin] = {
      title: data[origin]?.title ?? contractorOriginOptions[origin],
      inputs,
    }
  })
  return result
}

export const ContractorInputPropertyModal = ({
  fieldKey,
  fieldData,
  data,
  sectionOptions,
  onClose,
}: ContractorInputPropertyModalProps) => {
  const [inputs, setInputs] = useState<Record<string, InputType>>(contractorInputPropertyFields())

  const additionalFieldOrigins = useMemo(() => buildAdditionalFieldOrigins(data), [data])
  const isNewField = !fieldKey

  useEffect(() => {
    const next = contractorInputPropertyFields()
    Object.keys(next).forEach((key) => {
      if ((fieldData as any)[key] !== undefined) {
        next[key].value = (fieldData as any)[key]
      }
    })

    next.title.value = fieldData.title ?? ''
    next.inputType.value = capitalizeFirstLetter(fieldData.inputType || 'Text')
    next.inputType.visible = true
    next.type.value = fieldData.type || 'text'
    next.type.visible = true
    next.visible.value = fieldData.visible ?? true
    next.span.value = fieldData.span ?? 1
    next.span.visible = true

    if (sectionOptions) {
      next.section.visible = true
      next.section.options = sectionOptions
      next.section.value = fieldData.section || ''
    } else {
      next.section.visible = false
    }

    if (fieldData.options) {
      next.options.value = normalizeOptions(fieldData.options as InputOptions)
    }
    next.visibleLogic.options = {
      ...(next.visibleLogic.options as object),
      hasNotVisible: true,
      additionalFieldOrigins,
    }

    setInputs(next)
  }, [])

  const onChange = (key: string, value: any) => {
    const next = cloneDeep(inputs)
    value = InputConvert(next[key], value)
    next[key].error = InputValidate({ ...next[key], value })
    next[key].value = value
    if (key === 'inputType') {
      const normalized = capitalizeFirstLetter(value)
      if (normalized === 'Text') {
        next.type.value = 'text'
      }
    }
    setInputs(next)
  }

  const computedInputs = useMemo(() => {
    const next = cloneDeep(inputs)
    const it = String(next.inputType.value || '')
    const isText = it === 'Text'
    const isSelect = it === 'Select'

    next.type.visible = isText
    next.options.visible = isSelect
    next.defaultValue.visible = isText
    next.visibleLogic.options = {
      ...(next.visibleLogic.options as object),
      hasNotVisible: true,
      additionalFieldOrigins,
    }
    if (sectionOptions) {
      next.section.visible = true
      next.section.options = sectionOptions
    } else {
      next.section.visible = false
    }

    return next
  }, [inputs, additionalFieldOrigins, sectionOptions])

  const onSubmit = () => {
    const next = cloneDeep(computedInputs)
    let hasError = false
    Object.keys(next).forEach((key) => {
      if (next[key].visible === false) return
      next[key].error = InputValidate({ ...next[key], value: next[key].value })
      if (next[key].error?.length) hasError = true
    })
    setInputs(next)
    if (hasError) return

    const newData: ContractorInputType = { ...fieldData }
    Object.keys(next).forEach((key) => {
      if (next[key].visible === false) return
      ;(newData as any)[key] = next[key].value
    })

    const inputType = String(next.inputType.value || '')
    if (inputType === 'Text') {
      if (newData.type === 'dollar') {
        newData.prefix = '$'
        newData.type = 'thousandSep'
      } else if (newData.type === 'percentage') {
        newData.prefix = '%'
        newData.type = 'number'
      } else {
        delete newData.prefix
      }
    } else {
      delete newData.type
      delete newData.defaultValue
      delete newData.prefix
    }

    if (inputType === 'Select') {
      newData.hasDefaultOption = true
    } else {
      delete newData.options
      delete newData.hasDefaultOption
    }

    if (!newData.visibleLogic?.length) delete newData.visibleLogic
    if (!newData.section) delete newData.section

    onClose(newData)
  }

  const modalTitle = isNewField ? 'Add New Field' : fieldData.title || fieldKey

  return (
    <Modal isOpen title={modalTitle} titleClass="w-144" titleOkay="Submit" onClose={() => onClose()} onOk={onSubmit}>
      <div className="w-144">
        {Object.keys(computedInputs).map((key) => {
          const input = computedInputs[key]
          if (input.visible === false) return null
          return (
            <div key={key} className="mb-2">
              <RenderInput input={input} Key={key} onChange={onChange} />
            </div>
          )
        })}
      </div>
    </Modal>
  )
}
